import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const API = window.location.origin;

const GestionCartaBienvenida = () => {
  const [cartaActual, setCartaActual] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    cargarCartaBienvenida();
  }, []);

  const cargarCartaBienvenida = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/carta-bienvenida`);
      setCartaActual(response.data.carta);
    } catch (error) {
      console.log('No hay carta de bienvenida configurada');
    } finally {
      setCargando(false);
    }
  };

  const subirCarta = async () => {
    if (!archivo) {
      toast.error('Selecciona un archivo PDF');
      return;
    }

    if (archivo.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF');
      return;
    }

    if (archivo.size > 10 * 1024 * 1024) { // 10MB
      toast.error('El archivo no puede superar 10MB');
      return;
    }

    setSubiendo(true);
    try {
      const formData = new FormData();
      formData.append('archivo', archivo);

      const response = await axios.post(`${API}/api/admin/carta-bienvenida`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Carta de bienvenida subida exitosamente');
      setArchivo(null);
      cargarCartaBienvenida();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la carta');
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarCarta = async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar la carta de bienvenida actual?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/carta-bienvenida`);
      toast.success('Carta de bienvenida eliminada');
      setCartaActual(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar la carta');
    }
  };

  const descargarCarta = async () => {
    if (cartaActual) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/api/admin/carta-bienvenida/descargar`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'carta_bienvenida.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) { console.error(e); }
    }
  };

  if (cargando) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <i className="fas fa-file-pdf text-red-500 mr-3"></i>
            Gestión de Carta de Bienvenida
          </CardTitle>
          <CardDescription>
            Administra la carta de bienvenida que se mostrará a los estudiantes nuevos durante su registro
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Estado actual */}
          <div className="border rounded-lg p-4 bg-gray-50">
            <h3 className="font-semibold mb-2 flex items-center">
              <i className="fas fa-info-circle text-blue-500 mr-2"></i>
              Estado Actual
            </h3>
            {cartaActual ? (
              <div className="space-y-3">
                <p className="text-green-600 flex items-center">
                  <i className="fas fa-check-circle mr-2"></i>
                  Carta de bienvenida configurada
                </p>
                <div className="text-sm text-gray-600">
                  <p><strong>Archivo:</strong> {cartaActual.nombre_archivo}</p>
                  <p><strong>Subido:</strong> {new Date(cartaActual.fecha_subida).toLocaleString()}</p>
                  <p><strong>Tamaño:</strong> {(cartaActual.tamaño / 1024).toFixed(2)} KB</p>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={descargarCarta} variant="outline" size="sm">
                    <i className="fas fa-download mr-2"></i>
                    Descargar
                  </Button>
                  <Button onClick={eliminarCarta} variant="destructive" size="sm">
                    <i className="fas fa-trash mr-2"></i>
                    Eliminar
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-orange-600 flex items-center">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                No hay carta de bienvenida configurada
              </p>
            )}
          </div>

          {/* Subir nueva carta */}
          <div className="border rounded-lg p-4">
            <h3 className="font-semibold mb-4 flex items-center">
              <i className="fas fa-upload text-blue-500 mr-2"></i>
              {cartaActual ? 'Reemplazar Carta' : 'Subir Carta de Bienvenida'}
            </h3>
            
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setArchivo(e.target.files[0])}
                  className="hidden"
                  id="archivo-carta"
                />
                <label 
                  htmlFor="archivo-carta"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <i className="fas fa-cloud-upload-alt text-4xl text-gray-400"></i>
                  <span className="text-gray-600">
                    {archivo ? `Archivo seleccionado: ${archivo.name}` : 'Haz clic para seleccionar archivo PDF'}
                  </span>
                  <span className="text-xs text-gray-500">
                    Solo archivos PDF (máx. 10MB)
                  </span>
                </label>
              </div>

              {archivo && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <i className="fas fa-file-pdf text-red-500"></i>
                      <span className="text-sm font-medium">{archivo.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(archivo.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                    <Button 
                      onClick={() => setArchivo(null)}
                      variant="ghost" 
                      size="sm"
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                </div>
              )}

              <Button 
                onClick={subirCarta}
                disabled={!archivo || subiendo}
                className="w-full"
              >
                {subiendo ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Subiendo...
                  </>
                ) : (
                  <>
                    <i className="fas fa-upload mr-2"></i>
                    {cartaActual ? 'Reemplazar Carta' : 'Subir Carta'}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Información */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2 flex items-center">
              <i className="fas fa-lightbulb mr-2"></i>
              Información Importante
            </h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• La carta de bienvenida se mostrará a los estudiantes durante su primer registro</li>
              <li>• Solo se permiten archivos PDF con un tamaño máximo de 10MB</li>
              <li>• El archivo debe contener información sobre el Instituto y su finalidad</li>
              <li>• Los estudiantes podrán descargar y leer la carta antes de completar su registro</li>
            </ul>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default GestionCartaBienvenida;